import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { questions, ReputationResult, ReputationScores } from "./questions";
import { UserInfo } from "../../components/UserInfoForm";
import { webhookService } from "../../shared/webhookService";

interface ReputationQuizContainerProps {
  userInfo: UserInfo | null;
}

const ReputationQuizContainer: React.FC<ReputationQuizContainerProps> = ({
  userInfo,
}) => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<ReputationResult[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = async () => {
    if (selectedOption === null) return;

    const question = questions[currentQuestion];
    const selectedAnswerScores = question.options[selectedOption].scores;

    const newAnswer: ReputationResult = {
      questionId: question.id,
      selectedOption,
      scores: selectedAnswerScores,
    };

    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    } else {
      localStorage.setItem("reputation_results", JSON.stringify(newAnswers));

      // Prepare webhook data
      const webhookData = {
        quizName: "Reputation Quiz",
        userInfo: userInfo || {},
        responses: newAnswers.map((answer, index) => {
          const q = questions.find((q) => q.id === answer.questionId);
          return {
            questionNumber: index + 1,
            question: q?.question || "",
            answer: q?.options[answer.selectedOption]?.text || "",
          };
        }),
        timestamp: new Date().toISOString(),
      };

      // Send to webhook (with automatic retry on failure)
      await webhookService.submitQuizData(webhookData);

      navigate("/reputation/results");
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
      setSelectedOption(null);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const question = questions[currentQuestion];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          width: "100%",
        }}
      >
        {/* Progress Bar */}
        <div
          style={{
            marginBottom: "30px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "white",
              fontSize: "18px",
              marginBottom: "10px",
              fontWeight: "500",
            }}
          >
            Question {currentQuestion + 1} of {questions.length}
          </div>
          <div
            style={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "16px",
              marginBottom: "15px",
            }}
          >
            {Math.round(progress)}% Complete
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: "#c9a750",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: "40px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "30px",
              color: "#1a202c",
              lineHeight: "1.4",
            }}
          >
            {question.question}
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              marginBottom: "30px",
            }}
          >
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionSelect(index)}
                style={{
                  padding: "20px",
                  border:
                    selectedOption === index
                      ? "2px solid #c9a750"
                      : "2px solid #e2e8f0",
                  borderRadius: "12px",
                  backgroundColor:
                    selectedOption === index ? "#fffbf0" : "white",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  fontSize: "16px",
                }}
                onMouseEnter={(e) => {
                  if (selectedOption !== index) {
                    e.currentTarget.style.borderColor = "#cbd5e0";
                    e.currentTarget.style.backgroundColor = "#f7fafc";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption !== index) {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.backgroundColor = "white";
                  }
                }}
              >
                <span
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor:
                      selectedOption === index ? "#c9a750" : "#e2e8f0",
                    color: selectedOption === index ? "white" : "#4a5568",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  {String.fromCharCode(65 + index)}
                </span>
                <span
                  style={{
                    color: "#2d3748",
                    flex: 1,
                  }}
                >
                  {option.text}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "15px",
            }}
          >
            {currentQuestion > 0 && (
              <button
                onClick={handlePrevious}
                style={{
                  padding: "12px 30px",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  backgroundColor: "white",
                  color: "#4a5568",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f7fafc";
                  e.currentTarget.style.borderColor = "#cbd5e0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={selectedOption === null}
              style={{
                padding: "12px 30px",
                borderRadius: "8px",
                border: "none",
                backgroundColor:
                  selectedOption !== null ? "#c9a750" : "#cbd5e0",
                color: "white",
                fontSize: "16px",
                fontWeight: "500",
                cursor: selectedOption !== null ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
                marginLeft: currentQuestion === 0 ? "auto" : "0",
              }}
              onMouseEnter={(e) => {
                if (selectedOption !== null) {
                  e.currentTarget.style.backgroundColor = "#b8963d";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedOption !== null) {
                  e.currentTarget.style.backgroundColor = "#c9a750";
                }
              }}
            >
              {currentQuestion === questions.length - 1
                ? "See Results"
                : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReputationQuizContainer;
